import { useState, useEffect, useRef, useCallback } from 'react';

interface EventLogProps {
  alerts: {
    hasCrisis: boolean;
    hasSevere: boolean;
    directives: Array<{ signal: string; severity: string; emotionName: string }>;
  } | null;
}

interface LogEntry {
  id: string;
  signal: string;
  severity: string;
  emotionName: string;
  timestamp: Date;
}

const SEVERITY_ICON: Record<string, string> = {
  crisis: '🚨',
  severe: '⚠️',
  moderate: 'ℹ️',
  mild: '•',
  positive: '✨',
};

const SEVERITY_COLOR: Record<string, string> = {
  crisis: '#ef4444',
  severe: '#f97316',
  moderate: '#eab308',
  mild: '#94a3b8',
  positive: '#22c55e',
};

const MAX_ENTRIES = 50;
const DEDUP_WINDOW_MS = 10 * 60_000; // don't log same alert within 10 minutes

export function EventLog({ alerts }: EventLogProps) {
  const [entries, setEntries] = useState<LogEntry[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const lastSeenRef = useRef<Map<string, number>>(new Map());
  const panelRef = useRef<HTMLDivElement>(null);

  // Log new alerts as they come in
  useEffect(() => {
    if (!alerts?.directives?.length) return;
    const now = Date.now();
    const newEntries: LogEntry[] = [];

    for (const d of alerts.directives) {
      const key = `${d.signal}-${d.severity}`;
      const lastSeen = lastSeenRef.current.get(key) || 0;
      if (now - lastSeen < DEDUP_WINDOW_MS) continue;

      lastSeenRef.current.set(key, now);
      newEntries.push({
        id: `${key}-${now}`,
        signal: d.signal,
        severity: d.severity,
        emotionName: d.emotionName,
        timestamp: new Date(),
      });
    }

    if (newEntries.length > 0) {
      setEntries(prev => [...newEntries, ...prev].slice(0, MAX_ENTRIES));
      if (!isOpen) {
        setUnreadCount(prev => prev + newEntries.length);
      }
    }
  }, [alerts, isOpen]);

  // Close panel when clicking outside
  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen]);

  const toggleOpen = useCallback(() => {
    setIsOpen(prev => {
      if (!prev) setUnreadCount(0); // Mark all read when opening
      return !prev;
    });
  }, []);

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  return (
    <div className="event-log-wrapper" ref={panelRef}>
      <button
        className="event-log-btn"
        onClick={toggleOpen}
        title="Event Log"
        aria-label="Toggle event log"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <line x1="16" y1="13" x2="8" y2="13" />
          <line x1="16" y1="17" x2="8" y2="17" />
          <polyline points="10 9 9 9 8 9" />
        </svg>
        {unreadCount > 0 && (
          <span className="event-log-badge">{unreadCount > 9 ? '9+' : unreadCount}</span>
        )}
      </button>

      {isOpen && (
        <div className="event-log-panel">
          <div className="event-log-header">
            <span className="event-log-title">Event Log</span>
            <span className="event-log-count">{entries.length} events</span>
          </div>
          <div className="event-log-list">
            {entries.length === 0 ? (
              <div className="event-log-empty">No events yet</div>
            ) : (
              entries.map(entry => (
                <div key={entry.id} className="event-log-entry">
                  <span className="event-log-time">{formatTime(entry.timestamp)}</span>
                  <span className="event-log-icon">{SEVERITY_ICON[entry.severity] || '•'}</span>
                  <span className="event-log-text">
                    <span className="event-log-emotion" style={{ color: SEVERITY_COLOR[entry.severity] }}>
                      {entry.emotionName}
                    </span>
                    {' — '}
                    <span className="event-log-signal">{entry.signal}</span>
                    {' '}
                    <span className="event-log-severity" style={{ color: SEVERITY_COLOR[entry.severity] }}>
                      {entry.severity}
                    </span>
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
